import { useEffect } from 'react'
import { useWithdrawalSettings } from '@/hooks/use-withdrawal-settings'
import { useAppSelector } from '@/redux/hooks'
import { useTranslation } from 'react-i18next'
import {
  AnalyticsIcon,
  ConfigurationsIcon,
  DashboardsIcon,
  HomeIcon,
  BillingIcon,
  StoreIcon
} from '@itsrever/design-system'
import { useLocation } from 'react-router'
import { Roles } from '@/utils'
import {
  useExchangesShown,
  useReturnRateAnalyticsAllowed
} from '@/hooks/use-scope-platform'
import {
  NavbarHeader,
  NavbarFooter,
  NavItem,
  NavMenu
} from '@/ui/shared/layout'
import { clsx } from 'clsx'
import { useShowBilling } from '@/hooks'
import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import { useHasSizingData } from '@/application/queries'
import {
  isWarehousesV2Enabled,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { useReturnsAndRefundsV2 } from '@/hooks/use-returns-and-refunds-v2'
import {
  setGroupLevelExchangesPosthog,
  setGroupLevelReturnsAndRefundsPosthog,
  setGroupLevelWarehousesPosthog
} from '@/infrastructure/posthog'

enum Section {
  Home,
  Returns,
  Analytics,
  Configuration,
  Account,
  Integrations
}

export const Navbar = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const settings = useAppSelector((s) => s.settingsApi.settings.response)
  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)

  const { data: hasSizingData } = useHasSizingData(
    settings.shopify_settings?.store_id
  )

  const isAdmin = role === Roles.ReadWrite
  const isReviewer = role === Roles.Reviewer
  const isAnalyticsOnly = role === Roles.AnalyticsOnly
  const selectedEcommerce = useAppSelector(
    (s) => s.generalData.selectedEcommerce
  )
  const isReverLite = settings.use_case === PbECommerceSettingsUseCaseEnum.Lite

  const showHome = !isAnalyticsOnly
  const showReturns = !isAnalyticsOnly
  const showReturnRateAnalytics = useReturnRateAnalyticsAllowed()
  const showProductAnalytics = !isReverLite
  // Same rule as the blocked overlay's button: see useShowBilling.
  const showBilling = useShowBilling()
  const showAnalytics = !isReviewer
  // Shown in every view: its pages explain they need a store when none is picked.
  const showConfiguration = isAdmin
  // Warehouses only exists for groups on group-level warehouses.
  const { scope: warehousesScope } = useWarehousesScope()
  const { isFlagEnabled: isGroupLevelWarehouses } = useFeatureFlag(
    isWarehousesV2Enabled,
    {
      group: warehousesScope.kind === 'none' ? undefined : warehousesScope.group
    }
  )
  const showWarehouses = isAdmin && isGroupLevelWarehouses
  const exchangesShown = useExchangesShown()
  const showExchanges = isAdmin && exchangesShown
  // Refunds joins Exchanges in one entry for groups on the group-level refund configuration.
  const {
    conditions: isGroupLevelConditions,
    reasons: isGroupLevelReasons,
    refunds: isGroupLevelRefunds
  } = useReturnsAndRefundsV2()
  const showRefunds = isAdmin && isGroupLevelRefunds
  const groupLevelReturnsAndRefunds =
    isAdmin &&
    (isGroupLevelConditions || isGroupLevelReasons || isGroupLevelRefunds)
  useEffect(() => {
    if (showWarehouses) setGroupLevelWarehousesPosthog()
  }, [showWarehouses])
  useEffect(() => {
    if (showExchanges) setGroupLevelExchangesPosthog()
  }, [showExchanges])
  useEffect(() => {
    if (groupLevelReturnsAndRefunds) setGroupLevelReturnsAndRefundsPosthog()
  }, [groupLevelReturnsAndRefunds])
  const showAccount = !isReviewer && !isAnalyticsOnly
  // Installed integrations and webhooks are per portal, so the group view opens on its first
  // group-wide tab.
  const integrationsPath = selectedEcommerce
    ? '/account/integrations'
    : isAdmin
      ? '/account/integrations/api-keys'
      : '/account/integrations/mcp'
  const showSizing = !isReviewer && !isAnalyticsOnly && hasSizingData
  // Only where REVER turned the store's withdrawal form on; reviewers only see their warehouses' returns.
  const { enabled: showWithdrawals } = useWithdrawalSettings(
    isReviewer ? undefined : (selectedEcommerce ?? undefined)
  )

  const activeSection = calculateActiveSection()

  function calculateActiveSection() {
    if (location.pathname.includes('/returns')) return Section.Returns
    if (location.pathname.includes('/analytics')) return Section.Analytics
    if (location.pathname.includes('/configuration'))
      return Section.Configuration
    if (location.pathname.includes('/account')) return Section.Account
    if (location.pathname.includes('/integrations')) return Section.Integrations
    return Section.Home
  }

  return (
    <aside
      className={clsx('flex h-screen w-[240px] shrink-0 flex-col bg-shell')}
    >
      <NavbarHeader />
      <nav className="flex flex-col gap-1 overflow-y-auto p-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-shell [&::-webkit-scrollbar]:w-2">
        {showHome ? (
          <NavItem title={t('drawer_pages.home')} icon={<HomeIcon />} to="/" />
        ) : null}
        {showReturns ? (
          <NavMenu
            title={t('drawer_pages.returns')}
            icon={<DashboardsIcon />}
            isActive={activeSection === Section.Returns}
            pages={[
              {
                title: t('drawer_pages.orders'),
                to: '/returns/orders'
              },
              { title: t('drawer_pages.items'), to: '/returns/items' },
              showWithdrawals
                ? {
                    title: t('drawer_pages.withdrawals'),
                    to: '/returns/withdrawals'
                  }
                : null
            ]}
          />
        ) : null}
        {showSizing ? (
          <NavItem
            title={t('drawer_pages.sizing')}
            icon={
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 24 24"
                height="18px"
                width="18px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fill="none" d="M0 0h24v24H0z"></path>
                <path d="M21 8c-1.45 0-2.26 1.44-1.93 2.51l-3.55 3.56c-.3-.09-.74-.09-1.04 0l-2.55-2.55C12.27 10.45 11.46 9 10 9c-1.45 0-2.27 1.44-1.93 2.52l-4.56 4.55C2.44 15.74 1 16.55 1 18c0 1.1.9 2 2 2 1.45 0 2.26-1.44 1.93-2.51l4.55-4.56c.3.09.74.09 1.04 0l2.55 2.55C12.73 16.55 13.54 18 15 18c1.45 0 2.27-1.44 1.93-2.52l3.56-3.55c1.07.33 2.51-.48 2.51-1.93 0-1.1-.9-2-2-2z"></path>
                <path d="m15 9 .94-2.07L18 6l-2.06-.93L15 3l-.92 2.07L12 6l2.08.93zM3.5 11 4 9l2-.5L4 8l-.5-2L3 8l-2 .5L3 9z"></path>
              </svg>
            }
            to="size-suggestions"
          >
            <span className="font-logo rounded-sm bg-primary-main px-1.5 py-0.5 text-xs font-bold uppercase italic tracking-wider text-white">
              New
            </span>
          </NavItem>
        ) : null}
        {showAnalytics ? (
          <NavMenu
            title={t('drawer_pages.analytics')}
            icon={<AnalyticsIcon />}
            isActive={activeSection === Section.Analytics}
            pages={[
              {
                title: t('drawer_pages.compensation'),
                to: '/analytics/compensation'
              },
              showReturnRateAnalytics
                ? {
                    title: t('drawer_pages.return-rates'),
                    to: '/analytics/return-rates'
                  }
                : null,
              showProductAnalytics
                ? {
                    title: t('drawer_pages.product-analytics'),
                    to: '/analytics/products'
                  }
                : null,
              showProductAnalytics && showReturnRateAnalytics
                ? {
                    title: t('drawer_pages.product-return-rates'),
                    to: '/analytics/products-return-rates'
                  }
                : null,
              showProductAnalytics
                ? {
                    title: t('drawer_pages.all-products'),
                    to: '/analytics/all-products'
                  }
                : null
            ]}
          />
        ) : null}
        {showConfiguration || showWarehouses ? (
          <NavMenu
            title={t('drawer_pages.configuration')}
            icon={<ConfigurationsIcon />}
            isActive={activeSection === Section.Configuration}
            pages={[
              showConfiguration
                ? {
                    // Its other tabs keep the entry lit.
                    title: t(
                      isGroupLevelConditions
                        ? 'drawer_pages.return-conditions-group'
                        : 'drawer_pages.return-conditions'
                    ),
                    to: '/configuration/return-conditions',
                    alsoActiveOn: [
                      '/configuration/reasons',
                      '/configuration/non-returnable',
                      '/configuration/requirements'
                    ]
                  }
                : null,
              showRefunds
                ? {
                    title: t('drawer_pages.refunds-exchanges'),
                    to: '/configuration/refunds',
                    alsoActiveOn: ['/configuration/exchanges']
                  }
                : showExchanges
                  ? {
                      title: t('drawer_pages.exchanges'),
                      to: '/configuration/exchanges',
                      alsoActiveOn: [
                        '/configuration/exchanges/orders',
                        '/configuration/exchanges/instant-exchange'
                      ]
                    }
                  : null,
              showConfiguration
                ? {
                    title: t('drawer_pages.logistics'),
                    to: '/configuration/shipping'
                  }
                : null,
              showWarehouses
                ? {
                    // Routing rules is a tab of this page, so the entry stays lit on it.
                    title: t('drawer_pages.warehouses'),
                    to: '/configuration/warehouses',
                    alsoActiveOn: ['/configuration/warehouses/rules']
                  }
                : null,
              showConfiguration
                ? {
                    // Theme and Copies are one destination in the menu and two tabs on the page:
                    // both answer "how does my portal look and read".
                    title: t('drawer_pages.customisation'),
                    to: '/configuration/theme',
                    alsoActiveOn: [
                      '/configuration/copies',
                      '/configuration/withdrawal'
                    ]
                  }
                : null
            ]}
          />
        ) : null}
        {showAccount ? (
          <NavMenu
            title={t('drawer_pages.account')}
            icon={<StoreIcon />}
            isActive={activeSection === Section.Account}
            pages={[
              {
                title: t('drawer_pages.integrations'),
                to: integrationsPath,
                alsoActiveOn: ['/account/integrations']
              },
              {
                title: t('drawer_pages.store'),
                to: '/account/store'
              },
              {
                title: t('drawer_pages.referrals'),
                to: '/account/referrals'
              },
              // Admin-only: the page 404s for anyone else.
              isAdmin
                ? {
                    title: t('drawer_pages.users'),
                    to: '/account/users'
                  }
                : null
            ]}
          />
        ) : null}
        {showBilling ? (
          <NavItem
            title={t('drawer_pages.billing')}
            to="/billing"
            icon={<BillingIcon />}
          />
        ) : null}
      </nav>
      <NavbarFooter />
    </aside>
  )
}
